"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "refrigerator-size-calculator",
  "energy-cost-calculator",
  "kitchen-space-planner",
  "appliance-energy-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Dorm Mini Fridge Calculator",
  category: "Size & Capacity" as const,
  mediumDescription:
    "Pick a dorm mini fridge that fits your roommates, your meal plan, and your dorm's size cap. Most schools cap at 4.0 cu ft — we recommend a size range you can actually bring on move-in day.",
  faqs: [
    { question: "What's the typical dorm size cap for fridges?", answer: "Most US universities cap dorm refrigerators at 4.0 cu ft and 1.5 amps. Always check your housing contract — the cap varies and exceeding it is a common move-in confiscation reason." },
    { question: "Do I need a freezer compartment?", answer: "Only if you plan to store ice cream, frozen meals, or ice packs. A true freezer compartment is colder and better-sealed than a simple ice cube tray slot." },
    { question: "How many people can share a 4 cu ft mini fridge?", answer: "Two roommates if you eat most meals at the dining hall. For three or more sharing the same fridge, you'll likely outgrow 4 cu ft within a semester." },
    { question: "Should I get an Energy Star mini fridge?", answer: "Yes when possible. Energy Star compact refrigerators use 10–20% less electricity, which matters when the unit runs 24/7 in a small room." },
    { question: "How quiet do dorm fridges run?", answer: "Look for models rated 38–42 dB. Anything above 45 dB will be audible from a bed across the room — a real issue in shared sleeping quarters." },
  ],
  relatedTools: ["refrigerator-size-calculator", "energy-cost-calculator", "kitchen-space-planner"],
}

export function DormMiniFridgeCalculatorPageClient() {
  const [roommates, setRoommates] = useState("1")
  const [mealPlan, setMealPlan] = useState("full")
  const [needsFreezer, setNeedsFreezer] = useState(true)

  const result = useMemo(() => {
    // Bounds span the four select options — one occupant up to the "4+" room.
    const people = readCount(roommates, { min: 1, max: 4 })
    if (people === null) return null
    let minSize = 1.7
    let maxSize = 2.5
    if (people === 2) { minSize = 2.5; maxSize = 3.5 }
    else if (people >= 3) { minSize = 3.5; maxSize = 4.5 }
    if (mealPlan === "partial") { minSize += 0.5; maxSize += 0.7 }
    else if (mealPlan === "none") { minSize += 1.0; maxSize += 1.5 }
    if (needsFreezer) { minSize += 0.3; maxSize += 0.5 }
    let recommendedSize = ""
    if (maxSize <= 2.5) recommendedSize = "Small (1.7–2.5 cu ft)"
    else if (maxSize <= 4.0) recommendedSize = "Medium (2.5–4.0 cu ft)"
    else recommendedSize = "Large (4.0–4.5 cu ft) — check dorm cap"
    const overCap = maxSize > 4.0
    return {
      minSize: Math.round(minSize * 10) / 10,
      maxSize: Math.round(maxSize * 10) / 10,
      recommendedSize,
      overCap,
    }
  }, [roommates, mealPlan, needsFreezer])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Move-in day, no"
      heroTitleItalic="confiscation."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "Common dorm 4.0 cu ft cap"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "4.0", label: "Cu ft cap", note: "Most schools" },
            { value: "1.7", label: "Solo baseline", note: "Cu ft min" },
            { value: "+0.5", label: "Per partial meal", note: "Plan adj" },
            { value: "<42 dB", label: "Quiet target", note: "For sleep" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "dorm-mini-fridge-calculator")}
      cta={{
        title: "Find a compact fridge that fits your dorm.",
        body: "Browse refrigerators sortable by cubic feet — check the size against your dorm's cap.",
        buttonLabel: "Browse all refrigerators",
        buttonHref: "/best-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Three inputs.
                <br />
                One legal size.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Roommates, dining-hall use, and freezer needs — all factored against the 4.0 cu ft cap.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">Roommates sharing</label>
                  <select value={roommates} onChange={(e) => setRoommates(e.target.value)} className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white">
                    <option value="1">Just me</option>
                    <option value="2">2 people</option>
                    <option value="3">3 people</option>
                    <option value="4">4+ people</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">Meal plan</label>
                  <select value={mealPlan} onChange={(e) => setMealPlan(e.target.value)} className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white">
                    <option value="full">Full meal plan</option>
                    <option value="partial">Partial meal plan</option>
                    <option value="none">No meal plan</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={needsFreezer} onChange={(e) => setNeedsFreezer(e.target.checked)} className="w-5 h-5 accent-[#ff9951]" />
                    <span className="text-[14px] font-bold text-gray-900">Need a real freezer compartment</span>
                  </label>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Real freezer holds ice cream &lt; 0°F.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">Recommended range</div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.maxSize ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">cu ft</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendedSize ?? "Enter details to see size."}
                    {result?.overCap && (
                      <span className="block mt-2 text-[#ffb88a] font-bold">⚠ Above the typical 4.0 cu ft dorm cap. Verify with housing.</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Min size</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.minSize ?? "—"} cu ft</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Dorm cap</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">4.0 cu ft</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Pick the unit"
          title="Counter-depth fridges that fit dorm-room caps"
          description="Slim footprints under 4.0 cu ft — the legal sweet spot for most US dorms."
          collectionType="counter-depth"
          fallbackCollectionType="top-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                Real caps. <em className="italic text-[#ff9951]">Real</em> meals.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Sizing baseline derived from common university housing rules and AHAM compact-refrigerator guidance.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Roommate baseline", d: "1.7 cu ft solo, 2.5 for two, 3.5 for three+. Per AHAM compact sizing." },
                { n: "02", t: "Meal-plan adjustment", d: "Partial plan +0.5 cu ft, no plan +1.0 cu ft. You're storing more ingredients, not just snacks." },
                { n: "03", t: "Dorm cap check", d: "Most schools cap at 4.0 cu ft / 1.5 amps. We flag any rec above the cap so you can verify before move-in." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges for first apartments"
          description="When you graduate to a real kitchen — efficient, affordable, plenty of capacity."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
